import type { CSSProperties } from "react";

/** Brand names stay as written; generic chips carry a key under `marketing.migrationStage.chips`. */
export type MigrationChip =
  | { readonly kind: "brand"; readonly name: string; readonly picked?: true }
  | { readonly kind: "label"; readonly key: "netzlaufwerk" | "wordVorlagen" }
  | { readonly kind: "more" };

export type MigrationGroupKey = "erp" | "vorgang" | "email" | "dokumente" | "projekte";

export type MigrationGroup = {
  readonly key: MigrationGroupKey;
  /** Explicit rows: the desktop canvas never wraps, so connector anchors stay exact. */
  readonly rows: readonly (readonly MigrationChip[])[];
};

const b = (name: string, picked?: true): MigrationChip =>
  picked ? { kind: "brand", name, picked } : { kind: "brand", name };
const MORE: MigrationChip = { kind: "more" };

/** Card order top → bottom. Only the first card lights up: the plan card tells the rest of the week. */
export const MIGRATION_GROUPS: readonly MigrationGroup[] = [
  {
    key: "erp",
    rows: [
      [b("DOMUS"), b("PowerHaus", true), b("immoware24"), b("Impower"), b("WinCASA")],
      [b("imveon"), b("Karthago"), b("HausPerfekt"), b("ALCO"), b("SIDOMO")],
      [b("easimo"), MORE],
    ],
  },
  { key: "vorgang", rows: [[b("Casavi"), b("Facilioo"), b("etg24"), b("iDWELL"), b("Vulcavo"), MORE]] },
  { key: "email", rows: [[b("Outlook"), b("Gmail"), b("IMAP"), MORE]] },
  {
    key: "dokumente",
    rows: [
      [b("DocuWare"), b("d.velop"), b("SharePoint"), b("OneDrive"), b("Google Drive")],
      [{ kind: "label", key: "netzlaufwerk" }, { kind: "label", key: "wordVorlagen" }, MORE],
    ],
  },
  { key: "projekte", rows: [[b("Asana"), b("Trello"), b("Microsoft Planner"), MORE]] },
];

/** One working day each, Monday to Friday. */
export const MIGRATION_STEPS = ["stammdaten", "vorgaenge", "dokumente", "einrichten", "fertig"] as const;

/** Desktop canvas in design units; `--u` maps one unit to the real width. Mirrored in `migration.css`. */
export const CANVAS = { w: 1080, h: 590, boardW: 560, chatX: 640, top: 2, gap: 12 } as const;

/** Card height for n chip rows; mirrored in `migration.css` (`--rows`). */
export function cardHeight(rows: number): number {
  return 58 + rows * 28 + (rows - 1) * 8;
}

/** Vertical center of each card on the desktop canvas: where its connector starts. */
export const CARD_CENTERS: readonly number[] = MIGRATION_GROUPS.reduce<{ y: number; out: number[] }>(
  (acc, group) => {
    const h = cardHeight(group.rows.length);
    acc.out.push(acc.y + h / 2);
    acc.y += h + CANVAS.gap;
    return acc;
  },
  { y: CANVAS.top, out: [] },
).out;

/** One run, ms after it starts: PowerHaus lights up, then the week ticks off day by day. */
export const MIGRATION_TIMELINE = {
  /** PowerHaus card lights up, its wire starts to flow. */
  active: 500,
  stepFrom: 900,
  stepRun: 1000,
} as const;

export function stepStart(index: number): number {
  return MIGRATION_TIMELINE.stepFrom + index * MIGRATION_TIMELINE.stepRun;
}

/** Stagger for the keyframes in `migration.css`. */
export function delay(ms: number): CSSProperties {
  return { animationDelay: `${ms}ms` };
}
